import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CreateWorkspaceChatRequest,
  CreateWorkspaceChatResponse,
} from '@/features/chats/types/chat'
import { getPlanLimitNotification } from '@/shared/api/errors'
import { notificationActions } from '@/shared/notifications/notification-store'
import { apiClient } from '@/shared/api/client'
import { trackChatStarted } from '@/shared/analytics'
import { workspaceChatQueryKeys } from '../queries/chats-query'

interface CreateWorkspaceChatVariables {
  workspaceId: string
  payload: CreateWorkspaceChatRequest
}

export function useCreateWorkspaceChatMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['workspace-chat', 'create'],
    mutationFn: ({
      workspaceId,
      payload,
    }: CreateWorkspaceChatVariables): Promise<CreateWorkspaceChatResponse> =>
      apiClient.chats.createWorkspaceChat(workspaceId, payload),
    onSuccess: (chat, variables) => {
      queryClient.setQueryData<Array<CreateWorkspaceChatResponse>>(
        workspaceChatQueryKeys.list(variables.workspaceId),
        (current) => {
          const nextItems = current ? [...current] : []
          const existingIndex = nextItems.findIndex(
            (item) => item.id === chat.id,
          )

          if (existingIndex >= 0) {
            nextItems.splice(existingIndex, 1)
          }

          nextItems.unshift(chat)
          return nextItems
        },
      )

      trackChatStarted({ workspace_id: variables.workspaceId })
    },
    onError: async (error) => {
      const limitNotification = await getPlanLimitNotification(error)
      if (limitNotification) {
        notificationActions.info(
          limitNotification.title,
          limitNotification.description,
        )
      }
    },
  })
}
